import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Link as RouterLink,
  Navigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import AuthLayout from '../layouts/AuthLayout';
import FacilityLayout from '../layouts/FacilityLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import ImpersonationBanner from '../components/auth/ImpersonationBanner';
import { useDarkMode } from '../contexts/DarkModeContext';

/**
 * SS-SEC-024 — Pathless layout route at the router root. Mounts the
 * ImpersonationBanner above every page in the app so the persistent warning
 * shows regardless of whether the operator is on /support-session, /facilities,
 * /users, or a facility subroute. ImpersonationBanner is a no-op when the user
 * isn't impersonating, so it's cheap to mount globally.
 *
 * Lives inside the router tree (not above RouterProvider in App.tsx) because
 * the banner uses useNavigate() / useDispatch(), both of which require their
 * respective contexts.
 */
function RootLayout() {
  return (
    <>
      <ImpersonationBanner />
      <Outlet />
    </>
  );
}

/**
 * Frame for the personal account pages opened outside a facility
 * (/account/security, /account/notifications): page padding and a way back
 * to the facility picker. Inside a facility the same pages render in the
 * facility shell instead.
 */
function AccountPageShell({ children }: { children: ReactNode }) {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button component={RouterLink} to="/facilities" size="small" sx={{ mb: 2 }}>
        Back to facilities
      </Button>
      {children}
    </Container>
  );
}

// ---- Lazy pages ----
const Login = lazy(() => import('../pages/Login'));
const FacilitySelect = lazy(() => import('../pages/FacilitySelect'));
const CommandCenter = lazy(() => import('../pages/CommandCenter'));
const PredictedRisks = lazy(() => import('../pages/PredictedRisks'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const WorkerList = lazy(() => import('../pages/WorkerList'));
const WorkerDetail = lazy(() => import('../pages/WorkerDetail'));
const SafetyDashboard = lazy(() => import('../pages/SafetyDashboard'));
const ProductionDashboard = lazy(() => import('../pages/ProductionDashboard'));
const AlertsPanel = lazy(() => import('../pages/AlertsPanel'));
const CriticalAlertConsole = lazy(() => import('../pages/CriticalAlertConsole'));
const Reports = lazy(() => import('../pages/Reports'));
const FacilitySettings = lazy(() => import('../pages/FacilitySettings'));
const WorkerAppearanceSettings = lazy(() => import('../pages/WorkerAppearanceSettings'));
const FirmwareProvisioning = lazy(() => import('../pages/FirmwareProvisioning'));
const BatchProvisioning = lazy(() => import('../pages/BatchProvisioning'));
const Assign = lazy(() => import('../pages/Assign'));
const DeviceAssignments = lazy(() => import('../pages/DeviceAssignments'));
const RedZoneConfig = lazy(() => import('../pages/RedZoneConfig'));
const ProductionSettings = lazy(() => import('../pages/ProductionSettings'));
const OshaHoursSettings = lazy(() => import('../pages/OshaHoursSettings'));
const TradesAdmin = lazy(() => import('../pages/TradesAdmin'));
const AlertEscalationRules = lazy(() => import('../pages/AlertEscalationRules'));
const DeviceHealth = lazy(() => import('../pages/DeviceHealth'));
const VehicleManagement = lazy(() => import('../pages/VehicleManagement'));
const MobileDashboard = lazy(() => import('../pages/MobileDashboard'));
const MobileProvisioning = lazy(() => import('../pages/MobileProvisioning'));
const ErgoRiskDashboard = lazy(() => import('../pages/ErgoRiskDashboard'));
const ErgoWorkerBreakdown = lazy(() => import('../pages/ErgoWorkerBreakdown'));
const FatigueWorkerBreakdown = lazy(() => import('../pages/FatigueWorkerBreakdown'));
const FatigueMonitor = lazy(() => import('../pages/FatigueMonitor'));
const WeeklySchedule = lazy(() => import('../pages/WeeklySchedule'));
const RestrictedZoneActivity = lazy(() => import('../pages/RestrictedZoneActivity'));
const OperationsAnalytics = lazy(() => import('../pages/OperationsAnalytics'));
const ComparativeAnalytics = lazy(() => import('../pages/ComparativeAnalytics'));
const FloorPlanEditor = lazy(() => import('../pages/UnifiedFloorPlanEditor'));
const DeviceSetupWizard = lazy(() => import('../pages/DeviceSetupWizard'));
const OnboardingWizard = lazy(() => import('../pages/OnboardingWizard'));
const NotFound = lazy(() => import('../pages/NotFound'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const AcceptInvite = lazy(() => import('../pages/AcceptInvite'));
const Replay = lazy(() => import('../pages/Replay'));
const Signup = lazy(() => import('../pages/Signup'));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail'));
const Pricing = lazy(() => import('../pages/Pricing'));
const ContactSales = lazy(() => import('../pages/ContactSales'));
const Billing = lazy(() => import('../pages/Billing'));
const UserManagement = lazy(() => import('../pages/UserManagement'));
const SafetyIncidents = lazy(() => import('../pages/SafetyIncidents'));
const ApiKeyManagement = lazy(() => import('../pages/ApiKeyManagement'));
const MyPreferences = lazy(() => import('../pages/MyPreferences'));
const UwbCalibration = lazy(() => import('../pages/UwbCalibration'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const StatusPage = lazy(() => import('../pages/StatusPage'));
const Changelog = lazy(() => import('../pages/Changelog'));
const BrandingSettings = lazy(() => import('../pages/BrandingSettings'));
const OrganizationSettings = lazy(() => import('../pages/OrganizationSettings'));
const AccountSecurity = lazy(() => import('../pages/AccountSecurity'));
const NotificationSettings = lazy(() => import('../pages/NotificationSettings'));
const ConfirmEmailChange = lazy(() => import('../pages/ConfirmEmailChange'));
const SsoComplete = lazy(() => import('../pages/SsoComplete'));
const CancelEmailChange = lazy(() => import('../pages/CancelEmailChange'));
const TaskBuilder = lazy(() => import('../pages/TaskBuilder'));
// Platform-operator pages (PlatformAdmin, SupportSession, DemoControl,
// MLModelManagement, LiveLabeling) now live in the SEPARATE operator SPA
// (operator.html → src/operator/), served at platform-admin.sentrysolutions.info.
// They are intentionally NOT routed or imported here so the customer bundle no
// longer contains operator code or the /admin endpoint map.

const PAGE_LOAD_RECOVERY_MS = 12_000;
const APP_RESET_PATH = '/reset-app.html';

function PageLoadingFallback() {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), PAGE_LOAD_RECOVERY_MS);
    return () => clearTimeout(id);
  }, []);

  if (!timedOut) {
    return (
      <Box
        role="status"
        aria-live="polite"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={2}
        minHeight="60vh"
        px={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading page...
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={2}>
      <Alert severity="warning" sx={{ maxWidth: 560 }}>
        <Typography fontWeight={600} mb={0.5}>
          This page is taking too long to load.
        </Typography>
        <Typography variant="body2" mb={2}>
          A new app version may have loaded while your browser still had an older page cache. Refresh the page to load the current bundle.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
          <Button variant="outlined" onClick={() => window.location.assign(APP_RESET_PATH)}>
            Reset app cache
          </Button>
          <Button variant="outlined" onClick={() => window.location.assign('/login')}>
            Sign in again
          </Button>
        </Stack>
      </Alert>
    </Box>
  );
}

// ---- Shared Suspense wrapper ----
function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      {children}
    </Suspense>
  );
}

// ---- Error boundary element for route errors ----
function RouteErrorBoundary() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred';

  return (
    <Box p={4} textAlign="center">
      <Typography variant="h5" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

// ---- FacilityLayout wrapper (consumes darkMode from React context) ----
function FacilityLayoutWrapper() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  return (
    <FacilityLayout
      darkMode={darkMode}
      onToggleDarkMode={toggleDarkMode}
    />
  );
}

// ---- Router ----
// SS-SEC-024: every route is a child of the pathless RootLayout so the
// ImpersonationBanner is always above the page content.
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
  // Public
  {
    // Email/password sign-in page for all SentrySensor users
    path: '/login',
    element: (
      <AuthLayout>
        <PageSuspense>
          <Login />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Public auth flows
  {
    // Landing page after a SAML single sign-on: loads the new session, or shows an admin's test sign-in result (launch-readiness S13)
    path: '/sso/complete',
    element: (
      <AuthLayout>
        <PageSuspense>
          <SsoComplete />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Lets a user request a password-reset email by entering their address
    path: '/forgot-password',
    element: (
      <AuthLayout>
        <PageSuspense>
          <ForgotPassword />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Lets a user set a new password using a reset token from their email
    path: '/reset-password',
    element: (
      <AuthLayout>
        <PageSuspense>
          <ResetPassword />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Applies a pending sign-in email change from the confirmation link sent to the new address (launch-readiness S1)
    path: '/confirm-email-change',
    element: (
      <AuthLayout>
        <PageSuspense>
          <ConfirmEmailChange />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Stops a pending sign-in email change from the cancel link sent to the current address (launch-readiness S1)
    path: '/cancel-email-change',
    element: (
      <AuthLayout>
        <PageSuspense>
          <CancelEmailChange />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Lets an invited user accept an org invitation and set up their account
    path: '/accept-invite',
    element: (
      <AuthLayout>
        <PageSuspense>
          <AcceptInvite />
        </PageSuspense>
      </AuthLayout>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Self-service signup (Phase 2)
  {
    // Self-service account/org registration page for new prospective customers
    path: '/signup',
    element: (
      <PageSuspense>
        <Signup />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Confirms a new user's email address via a verification link/token
    path: '/verify-email',
    element: (
      <PageSuspense>
        <VerifyEmail />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public marketing page showing plan tiers and pricing for prospects
    path: '/pricing',
    element: (
      <PageSuspense>
        <Pricing />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public contact-sales lead form for prospects requesting a demo or quote
    path: '/contact-sales',
    element: (
      <PageSuspense>
        <ContactSales />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public Terms of Service legal page for all visitors
    path: '/terms',
    element: (
      <PageSuspense>
        <TermsOfService />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public Privacy Policy legal page for all visitors
    path: '/privacy',
    element: (
      <PageSuspense>
        <PrivacyPolicy />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public system status page showing service uptime and incident history
    path: '/status',
    element: (
      <PageSuspense>
        <StatusPage />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Public changelog page listing product releases and feature updates
    path: '/changelog',
    element: (
      <PageSuspense>
        <Changelog />
      </PageSuspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  // First-run tenant onboarding wizard (requires auth, no facility context yet).
  // New signups land here from /verify-email until onboarding is completed.
  {
    // First-run setup wizard guiding a new tenant admin through initial org configuration
    path: '/onboarding',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
        <PageSuspense>
          <OnboardingWizard />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Facility picker (requires auth)
  {
    // Facility picker listing the facilities a signed-in user can access
    path: '/facilities',
    element: (
      <ProtectedRoute>
        <PageSuspense>
          <FacilitySelect />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Personal account pages outside a facility (every signed-in role; linked from the
  // Header user menu when no facility is in context). Launch-readiness S7.
  {
    // The signed-in user's own password, sign-in email and two-factor authentication, for every role, outside a facility
    path: '/account/security',
    element: (
      <ProtectedRoute>
        <AccountPageShell>
          <PageSuspense>
            <AccountSecurity />
          </PageSuspense>
        </AccountPageShell>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // The signed-in user's own display units (metric or imperial, decision S4), for every role, outside a facility
    path: '/account/preferences',
    element: (
      <ProtectedRoute>
        <AccountPageShell>
          <PageSuspense>
            <MyPreferences />
          </PageSuspense>
        </AccountPageShell>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // The signed-in user's own notification preferences, for every role, outside a facility
    path: '/account/notifications',
    element: (
      <ProtectedRoute>
        <AccountPageShell>
          <PageSuspense>
            <NotificationSettings />
          </PageSuspense>
        </AccountPageShell>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Org-level user management — admins only. Replaces the prior workflow of
  // editing the `users` table directly in the database.
  {
    // Org-level user management page for admins to invite, edit, and deactivate users
    path: '/users',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN']}>
        <PageSuspense>
          <UserManagement />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // NFC harness-assignment landing page (phone opens from a tag). Top-level so
  // the bare /assign?neck=&waist= URL works cold. The UI role gate matches the
  // harness write endpoints; ProtectedRoute preserves this URL through login.
  {
    // Role-gated NFC harness handoff that preserves the complete deep link through sign-in
    path: '/assign',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN']}>
        <PageSuspense>
          <Assign />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // API keys management — ADMIN/SUPER_ADMIN only
  {
    // API key management page for admins to create and revoke programmatic access keys
    path: '/settings/api-keys',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
        <PageSuspense>
          <ApiKeyManagement />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  {
    // Org-level billing page for admins to view plan status, invoices, and Stripe portal access
    path: '/billing',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN']}>
        <PageSuspense>
          <Billing />
        </PageSuspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // NOTE: /support-session and /platform-admin moved to the separate operator SPA
  // (operator.html, served at platform-admin.sentrysolutions.info). The
  // ImpersonationBanner stays here (RootLayout) because an impersonated session is
  // VIEWED in the customer app after being started from the operator app.

  // Facility shell
  {
    // Facility shell that validates access, tears down blocked realtime, and
    // hosts all per-facility pages for the selected facility
    path: '/facilities/:facilityId',
    element: (
      <ProtectedRoute>
        <FacilityLayoutWrapper />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        // Executive command-center overview of facility-wide safety, risk, and activity status
        path: 'command-center',
        element: (
          // UI-015: route was ungated; gate to the Atlas-declared roles (matches the
          // identically-gated sibling predicted-risks) so a direct URL can't bypass it.
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN']}>
            <PageSuspense>
              <CommandCenter />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // ML-driven predicted-risk view forecasting injury/incident likelihood for workers
        path: 'predicted-risks',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN']}>
            <PageSuspense>
              <PredictedRisks />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Live floor view showing realtime worker positions and active alerts for the facility
        path: 'dashboard',
        element: (
          <PageSuspense>
            <Dashboard />
          </PageSuspense>
        ),
      },
      {
        // Roster of all workers in the facility with status and quick links to detail
        path: 'workers',
        element: (
          <PageSuspense>
            <WorkerList />
          </PageSuspense>
        ),
      },
      {
        // Per-worker detail page with location history, ergonomics, and assigned device
        path: 'workers/:workerId',
        element: (
          <PageSuspense>
            <WorkerDetail />
          </PageSuspense>
        ),
      },
      {
        // Safety dashboard with Motion Class risk cards and per-worker ergonomic trends
        path: 'safety',
        element: (
          // Route was ungated though Safety targets ADMIN/SUPERVISOR (+ the global SUPER_ADMIN
          // bypass ProtectedRoute applies to every gated page); matches sibling safety-incidents.
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <SafetyDashboard />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Safety incident log for admins/supervisors to review and manage recorded incidents
        path: 'safety-incidents',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <SafetyIncidents />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Ergonomic risk dashboard tracking posture and biomechanical strain across workers
        path: 'ergonomics',
        element: (
          <PageSuspense>
            <ErgoRiskDashboard />
          </PageSuspense>
        ),
      },
      {
        // Per-worker ergonomic breakdown drill-down (five Motion Class signals, trunk-loading components, avatar)
        path: 'ergonomics/worker/:workerId',
        element: (
          <PageSuspense>
            <ErgoWorkerBreakdown />
          </PageSuspense>
        ),
      },
      {
        // Fatigue monitor surfacing worker exhaustion indicators and shift-load trends
        path: 'fatigue',
        element: (
          <PageSuspense>
            <FatigueMonitor />
          </PageSuspense>
        ),
      },
      {
        // Per-worker fatigue breakdown drill-down (contributing factors, recovery debt, trend)
        path: 'fatigue/worker/:workerId',
        element: (
          <PageSuspense>
            <FatigueWorkerBreakdown />
          </PageSuspense>
        ),
      },
      {
        // Weekly schedule view of worker shifts and staffing for the facility
        path: 'schedule',
        element: (
          <PageSuspense>
            <WeeklySchedule />
          </PageSuspense>
        ),
      },
      {
        // Restricted-zone activity report of worker entries into off-limits or hazardous areas
        path: 'restricted-zones',
        element: (
          <PageSuspense>
            <RestrictedZoneActivity />
          </PageSuspense>
        ),
      },
      {
        // Production dashboard showing facility throughput and output metrics
        path: 'production',
        element: (
          <PageSuspense>
            <ProductionDashboard />
          </PageSuspense>
        ),
      },
      {
        // Alerts panel listing active and historical safety/system alerts for the facility
        path: 'alerts',
        element: (
          <PageSuspense>
            <AlertsPanel />
          </PageSuspense>
        ),
      },
      {
        // Keep this static segment explicitly above alerts/:alertId so "console"
        // cannot be captured as an alertId. React Router ranks static segments
        // first, but the ordering is intentionally kept explicit.
        path: 'alerts/console',
        element: (
          <PageSuspense>
            <CriticalAlertConsole />
          </PageSuspense>
        ),
      },
      {
        // Critical Alerts Console — per-incident forensic deep view (queue rail,
        // escalation clock, sensor record, response timeline, actions)
        path: 'alerts/:alertId',
        element: (
          <PageSuspense>
            <CriticalAlertConsole />
          </PageSuspense>
        ),
      },
      {
        // Reports page for generating and exporting facility safety and activity reports
        path: 'reports',
        element: (
          <PageSuspense>
            <Reports />
          </PageSuspense>
        ),
      },
      {
        // Operations analytics dashboard for facility summary metrics and worker movement heatmaps
        path: 'analytics/operations',
        element: (
          <PageSuspense>
            <OperationsAnalytics />
          </PageSuspense>
        ),
      },
      {
        // Comparative analytics view for benchmarking metrics across periods, zones, or teams
        path: 'analytics/compare',
        element: (
          // UI-030: route was ungated despite the declared ADMIN/SUPER_ADMIN restriction;
          // gate it explicitly with the ADMIN/SUPER_ADMIN roles declared for this page.
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <ComparativeAnalytics />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Facility settings hub for admins/supervisors to configure the facility
        path: 'settings',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <FacilitySettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      // settings/zones and settings/anchors were removed (F17-16, decision S10):
      // the Floor Plan Editor (settings/floor-plan) owns zones and anchors.
      {
        // Worker appearance settings for customizing 3D avatar/model display per worker
        path: 'settings/worker-models',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <WorkerAppearanceSettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Guided device-setup wizard for onboarding new devices.
        // ADMIN/SUPER_ADMIN — the final provisioning POST is ADMIN-gated on the
        // backend, so a SUPERVISOR could previously complete the whole wizard
        // and then 403 at the last step.
        path: 'settings/device-setup',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <DeviceSetupWizard />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Firmware provisioning settings for flashing and configuring device firmware.
        // ADMIN/SUPER_ADMIN — aligned with the page's own role check and the
        // backend's provisioning authorize() (SUPERVISOR previously passed the
        // route gate only to hit the page's hard block).
        path: 'settings/firmware',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <FirmwareProvisioning />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Batch provisioning settings for admins to provision many devices at once
        path: 'settings/batch-provisioning',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <PageSuspense>
              <BatchProvisioning />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Device Assignments desk: assign harnesses to workers from a keyboard.
        // Shares the /devices/harness/* endpoints with the NFC /assign tap page.
        path: 'settings/device-assignments',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN']}>
            <PageSuspense>
              <DeviceAssignments />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Static floor-plan configuration for admins; live workers and realtime subscriptions stay on Dashboard
        path: 'settings/floor-plan',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <PageSuspense>
              <FloorPlanEditor />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Red-zone alert settings for configuring entry alerts on restricted/hazardous zones
        path: 'settings/red-zone-alerts',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <RedZoneConfig />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Production settings for configuring throughput targets and output tracking
        path: 'settings/production',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <ProductionSettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // OSHA hours worked and average employees per month, the only denominator of OSHA rates (admins only)
        path: 'settings/osha',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <OshaHoursSettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Trades admin settings for managing worker trade/role categories in the facility
        path: 'settings/trades',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <TradesAdmin />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Alert escalation rules settings for admins to define alert routing and escalation
        path: 'settings/escalation-rules',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <PageSuspense>
              <AlertEscalationRules />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // UWB calibration — anchor range bias + antenna delays + map-scale gate.
        // ADMIN/SUPER_ADMIN only, matching the backend authorize() on the
        // /calibration/uwb-* routes.
        path: 'settings/calibration',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <UwbCalibration />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Branding settings for admins/super-admins to customize logos, colors, and theme
        path: 'settings/branding',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <BrandingSettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Organization settings for admins/super-admins to manage tenant profile and privacy data lifecycle
        path: 'settings/organization',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <OrganizationSettings />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // The signed-in user's own password, sign-in email and two-factor authentication; open to every role (launch-readiness S7)
        path: 'settings/account-security',
        element: (
          <PageSuspense>
            <AccountSecurity />
          </PageSuspense>
        ),
      },
      {
        // The signed-in user's own display units (metric or imperial); open to every role (decision S4)
        path: 'settings/preferences',
        element: (
          <PageSuspense>
            <MyPreferences />
          </PageSuspense>
        ),
      },
      {
        // Notification settings for users to configure alert delivery channels and preferences; open to every role (launch-readiness S7)
        path: 'settings/notifications',
        element: (
          <PageSuspense>
            <NotificationSettings />
          </PageSuspense>
        ),
      },
      // settings/ml-models moved to the operator SPA (platform-admin.sentrysolutions.info).
      {
        // The one device inventory (Device Health) for admins and supervisors, with status, current battery, holders, battery health and anchor ranges
        path: 'device-health',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <DeviceHealth />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Vehicle registry and operator assignment for vehicle-worker proximity safety workflows
        path: 'vehicles',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <VehicleManagement />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Phase 4 Tasks — author task definitions and view detected occurrences
        path: 'tasks',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']}>
            <PageSuspense>
              <TaskBuilder />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
      {
        // Replay page to scrub back through a facility's recorded positions and activity; open to every role (Justin 2026-09-26, launch-readiness F15-8)
        path: 'replay',
        element: (
          <PageSuspense>
            <Replay />
          </PageSuspense>
        ),
      },
      // demo-control moved to the operator SPA (platform-admin.sentrysolutions.info).
      {
        // Mobile-optimized dashboard giving field users a condensed facility status view
        path: 'mobile',
        element: (
          <PageSuspense>
            <MobileDashboard />
          </PageSuspense>
        ),
      },
      {
        // Mobile provisioning flow for field admins to set up and pair devices
        // on the go. ADMIN/SUPER_ADMIN — it hands off into the Device Setup
        // wizard whose provisioning POSTs are admin-gated on the backend.
        path: 'mobile/provision',
        element: (
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <PageSuspense>
              <MobileProvisioning />
            </PageSuspense>
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Root
  { path: '/', element: <Navigate to="/facilities" replace /> },

  // 404
  {
    // 404 catch-all — renders NotFound for unmatched routes
    path: '*',
    element: (
      <PageSuspense>
        <NotFound />
      </PageSuspense>
    ),
  },
    ], // end of RootLayout children
  },
]);
